import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { addHouse, getHouseEntity, updateHouse } from '../../api_calls/api_calls_houses';
import { getBillingMetadata, upsertBillingMetadata } from '../../api_calls/api_calls_invoices';
import { useRefreshHouses } from '../../hooks/useRefreshHouses';
import { useUserContext } from '../../context/UserContext';
import axios from 'axios';
import { useTheme } from '@mui/material/styles';

import {
    Typography, TextField, Snackbar, Box, Button, Card, CardContent, Divider, Fade, CssBaseline, Avatar, InputLabel, Select, MenuItem, FormControl, Alert, Stack, Grid, Container, InputAdornment, Chip
} from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import SearchIcon from '@mui/icons-material/Search';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import LoadingFullPage from '../../components/layout/LoadingFullPage';

const HouseForm = () => {
    const { id } = useParams();
    const isEditMode = Boolean(id);
    const { token } = useUserContext();
    const navigate = useNavigate();
    const { refreshHouses } = useRefreshHouses();
    const theme = useTheme();

    const [plans, setPlans] = useState([]);
    const [filteredPlans, setFilteredPlans] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [plansLoading, setPlansLoading] = useState(false);
    const [formValues, setFormValues] = useState({
        name: '',
        address: '',
        area: '',
        year: '',
        floor: '',
        energyClass: '',
        heatingType: '',
        coolingType: '',
        invoiceProvidername: '',
        invoicename: '',
    });
    const [formErrors, setFormErrors] = useState({});
    const [backDrop, setBackDrop] = useState(false);
    const [success, setSuccess] = useState(false);
    const [failure, setFailure] = useState(false);
    const [billingMetadata, setBillingMetadata] = useState({
        dteuroanatm: '',
        dfeuroanatm: '',
        timizonis: '',
        palaiotita: '',
        synttap: '',
        nightmeter: false,
        simfisxyspar: '',
    });
    const [billingMetadataLoading, setBillingMetadataLoading] = useState(false);

    useEffect(() => {
        if (!token) {
            navigate('/signin');
            return;
        }

        // Fetch plans
        setPlansLoading(true);
        axios.get('/invoice/getall')
            .then((response) => {
                setPlans(response.data || []);
                setFilteredPlans(response.data || []);
                setPlansLoading(false);
            })
            .catch((error) => {
                console.error('Failed to load plans:', error);
                setPlansLoading(false);
            });

        if (isEditMode) {
            getHouseEntity(id)
                .then(async (res) => {
                    setFormValues({
                        name: res.name || '',
                        address: res.address || '',
                        area: res.area ? res.area.toString() : '',
                        year: res.year ? res.year.toString() : '',
                        floor: res.floor || '',
                        energyClass: res.energyclass || '',
                        heatingType: res.heatingtype || '',
                        coolingType: res.coolingtype || '',
                        invoiceProvidername: res.invoiceprovidername || '',
                        invoicename: res.invoicename || '',
                    });
                    // Find and set selected plan
                    if (res.invoiceprovidername && res.invoicename) {
                        const planResponse = await axios.get('/invoice/getall');
                        const plan = planResponse.data?.find(
                            p => p.provider_name === res.invoiceprovidername && p.name === res.invoicename
                        );
                        if (plan) {
                            setSelectedPlan(plan);
                        }
                    }
                    // Fetch billing metadata
                    setBillingMetadataLoading(true);
                    try {
                        const metadata = await getBillingMetadata(id);
                        setBillingMetadata({
                            dteuroanatm: metadata.dteuroanatm !== undefined && metadata.dteuroanatm !== null ? metadata.dteuroanatm.toString() : '',
                            dfeuroanatm: metadata.dfeuroanatm !== undefined && metadata.dfeuroanatm !== null ? metadata.dfeuroanatm.toString() : '',
                            timizonis: metadata.timizonis !== undefined && metadata.timizonis !== null ? metadata.timizonis.toString() : '',
                            palaiotita: metadata.palaiotita !== undefined && metadata.palaiotita !== null ? metadata.palaiotita.toString() : '',
                            synttap: metadata.synttap !== undefined && metadata.synttap !== null ? metadata.synttap.toString() : '',
                            nightmeter: metadata.nightmeter !== undefined && metadata.nightmeter !== null ? metadata.nightmeter : false,
                            simfisxyspar: metadata.simfisxyspar !== undefined && metadata.simfisxyspar !== null ? metadata.simfisxyspar.toString() : '',
                        });
                    } catch (error) {
                        console.error('Failed to load billing metadata:', error);
                    } finally {
                        setBillingMetadataLoading(false);
                    }
                })
                .catch(() => setFailure(true));
        }
    }, [id, isEditMode, token, navigate]);

    useEffect(() => {
        refreshHouses();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormValues(prev => ({ ...prev, [name]: value }));
        setFormErrors(prev => ({ ...prev, [name]: false }));
    };

    const handleBillingMetadataChange = (e) => {
        const { name, value, type, checked } = e.target;
        setBillingMetadata(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleNumericBillingChange = (e) => {
        const { name, value } = e.target;
        // Allow empty string, or non-negative numbers
        if (value === '' || (!isNaN(value) && parseFloat(value) >= 0)) {
            setBillingMetadata(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };

    useEffect(() => {
        let filtered = plans;

        // Filter by search query if present
        if (searchQuery.trim()) {
            const query = searchQuery.toLowerCase();
            filtered = plans.filter(plan => {
                const displayName = (plan.display_name || '').toLowerCase();
                const providerName = (plan.provider_name || '').toLowerCase();
                const description = (plan.description || '').toLowerCase();
                return displayName.includes(query) || providerName.includes(query) || description.includes(query);
            });
        }

        // Sort: current plan first
        const currentPlanIndex = filtered.findIndex(
            plan => plan.provider_name === formValues.invoiceProvidername && plan.name === formValues.invoicename
        );
        if (currentPlanIndex > 0) {
            const currentPlan = filtered[currentPlanIndex];
            filtered = [currentPlan, ...filtered.filter((_, i) => i !== currentPlanIndex)];
        }

        setFilteredPlans(filtered);
    }, [searchQuery, plans, formValues.invoiceProvidername, formValues.invoicename]);

    const handlePlanSelect = (plan) => {
        setSelectedPlan(plan);
        setFormValues(prev => ({
            ...prev,
            invoiceProvidername: plan.provider_name,
            invoicename: plan.name
        }));
    };

    const getColorCode = (color) => {
        return color === 'green' ? '#4CAF50' : '#2196F3';
    };

    const getBgColorCode = (color) => {
        return color === 'green' ? 'rgba(76, 175, 80, 0.1)' : 'rgba(33, 150, 243, 0.1)';
    };

    const validateForm = (values) => {
        const errors = {};
        if (!values.name) errors.name = true;
        if (!values.address) errors.address = true;
        return errors;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const errors = validateForm(formValues);
        if (Object.keys(errors).length) {
            setFormErrors(errors);
            return;
        }
        setBackDrop(true);
        const house = {
            ...formValues,
            area: formValues.area ? parseInt(formValues.area) : undefined,
            year: formValues.year ? parseInt(formValues.year) : undefined,
            typehouse: 'Single-Family',
        };
        try {
            if (isEditMode) {
                await updateHouse(id, house);
                // Update billing metadata
                const metadataToSave = {
                    dteuroanatm: parseFloat(billingMetadata.dteuroanatm) || 0,
                    dfeuroanatm: parseFloat(billingMetadata.dfeuroanatm) || 0,
                    timizonis: parseFloat(billingMetadata.timizonis) || 0,
                    palaiotita: parseFloat(billingMetadata.palaiotita) || 0,
                    synttap: parseFloat(billingMetadata.synttap) || 0,
                    nightmeter: billingMetadata.nightmeter || false,
                    simfisxyspar: parseFloat(billingMetadata.simfisxyspar) || 0
                };
                await upsertBillingMetadata(id, metadataToSave);
            } else {
                await addHouse(house);
            }
            setSuccess(true);
            setTimeout(() => {
                setSuccess(false);
                navigate('/');
            }, 2000);
        } catch (error) {
            setFailure(true);
        } finally {
            setBackDrop(false);
        }
    };

    const handleCloseSnackbar = () => {
        setFailure(false);
        navigate('/');
    };

    return (
        <>
            <CssBaseline />
            <Container maxWidth="md" sx={{ py: 5 }}>
                <Fade in timeout={800}>
                    <Card sx={{ width: '100%', maxWidth: { xs: 420, sm: 600, md: 900, lg: 1000 }, mx: 'auto', borderRadius: 4, boxShadow: 6, p: 2, background: 'rgba(255,255,255,0.95)' }}>
                        <CardContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                            <Avatar sx={{ bgcolor: 'secondary.main', mx: 'auto', boxShadow: 3, width: 56, height: 56 }}>
                                <HomeIcon fontSize="large" />
                            </Avatar>
                            <Typography component="h1" variant="h5" sx={{ mt: 2, mb: 1, fontWeight: 600 }}>{isEditMode ? 'Edit House' : 'Add House'}</Typography>
                            <Divider sx={{ width: '80%', my: 2 }} />
                            <Box component="form" noValidate autoComplete="off" onSubmit={handleSubmit} sx={{ mt: 1, width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                <Grid container spacing={2} sx={{ width: '100%' }}>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth required name="name" label="House Name" variant="outlined"
                                            placeholder="e.g. Dei Home"
                                            error={formErrors.name} onChange={handleChange} value={formValues.name}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth required name="address" label="Address" variant="outlined"
                                            placeholder="e.g. Papadopoulou 10, Athens"
                                            error={formErrors.address} onChange={handleChange} value={formValues.address}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <TextField
                                            fullWidth name="area" label="Square meters" type="number"
                                            onChange={handleChange} value={formValues.area}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <TextField
                                            fullWidth name="year" label="Year of construction" type="number"
                                            onChange={handleChange} value={formValues.year}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <FormControl fullWidth>
                                            <InputLabel>Floor</InputLabel>
                                            <Select name="floor" value={formValues.floor} onChange={handleChange} label="Floor">
                                                <MenuItem value="Basement">Basement</MenuItem>
                                                <MenuItem value="Ground floor">Ground floor</MenuItem>
                                                <MenuItem value="Greater than 1">Greater than 1</MenuItem>
                                                <MenuItem value="Roof">Roof</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <FormControl fullWidth>
                                            <InputLabel>Energy Class</InputLabel>
                                            <Select name="energyClass" value={formValues.energyClass} onChange={handleChange} label="Energy Class">
                                                {['A+', 'A', 'B+', 'B', 'C', 'D', 'E', 'F', 'H', 'No Energy Certificate.'].map((val) => (
                                                    <MenuItem key={val} value={val}>{val}</MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <FormControl fullWidth>
                                            <InputLabel>Heating Type</InputLabel>
                                            <Select name="heatingType" value={formValues.heatingType} onChange={handleChange} label="Heating Type">
                                                {['Oil boiler', 'Natural Gas Boiler', 'Pump', 'Electric device', 'Gas'].map((val) => (
                                                    <MenuItem key={val} value={val}>{val}</MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <FormControl fullWidth>
                                            <InputLabel>Cooling Type</InputLabel>
                                            <Select name="coolingType" value={formValues.coolingType} onChange={handleChange} label="Cooling Type">
                                                {['Split units', 'Central unit', 'Fan', 'Central'].map((val) => (
                                                    <MenuItem key={val} value={val}>{val}</MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
                                            Electricity Plan
                                        </Typography>
                                        <TextField
                                            fullWidth
                                            placeholder="Search Plans..."
                                            value={searchQuery}
                                            onChange={(e) => setSearchQuery(e.target.value)}
                                            sx={{
                                                mb: 2,
                                                '& .MuiOutlinedInput-root': {
                                                    borderRadius: '12px',
                                                }
                                            }}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <SearchIcon sx={{ color: theme.palette.text.secondary }} />
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                        <Box sx={{ maxHeight: '300px', overflowY: 'auto', p: 1, border: `1px solid ${theme.palette.grey[300]}`, borderRadius: 2 }}>
                                            {plansLoading ? (
                                                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                                                    Loading plans...
                                                </Typography>
                                            ) : filteredPlans.length === 0 ? (
                                                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                                                    {searchQuery ? 'No plans found matching your search.' : 'No plans available.'}
                                                </Typography>
                                            ) : (
                                                filteredPlans.map((plan, index) => {
                                                    const isSelected = selectedPlan?.provider_name === plan.provider_name && selectedPlan?.name === plan.name;
                                                    return (
                                                        <Box
                                                            key={index}
                                                            onClick={() => handlePlanSelect(plan)}
                                                            sx={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: 2,
                                                                padding: '12px',
                                                                marginBottom: '8px',
                                                                backgroundColor: isSelected ? theme.palette.primary.light + '20' : 'white',
                                                                borderRadius: '12px',
                                                                border: `1px solid ${isSelected ? theme.palette.primary.main : theme.palette.grey[200]}`,
                                                                cursor: 'pointer',
                                                                transition: 'all 0.2s',
                                                                '&:hover': {
                                                                    backgroundColor: theme.palette.grey[50],
                                                                    borderColor: theme.palette.primary.main,
                                                                    transform: 'translateY(-2px)',
                                                                    boxShadow: `0 4px 8px ${theme.palette.grey[300]}`,
                                                                },
                                                            }}
                                                        >
                                                            <Avatar
                                                                src={`/images/providers/${plan.provider_name}.png`}
                                                                alt={plan.provider_name}
                                                                sx={{
                                                                    width: 48,
                                                                    height: 48,
                                                                    bgcolor: getBgColorCode(plan.color),
                                                                    border: `2px solid ${getColorCode(plan.color)}`,
                                                                }}
                                                            />
                                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                                <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
                                                                    {plan.display_name}
                                                                </Typography>
                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                                                    <Chip
                                                                        label={(plan.provider_name || '').replace(/_/g, ' ')}
                                                                        size="small"
                                                                        sx={{
                                                                            backgroundColor: getBgColorCode(plan.color),
                                                                            color: getColorCode(plan.color),
                                                                            fontWeight: 600,
                                                                            fontSize: '0.65rem',
                                                                            height: '20px',
                                                                        }}
                                                                    />
                                                                </Box>
                                                                {plan.description && (
                                                                    <Typography variant="caption" color="text.secondary">
                                                                        {plan.description}
                                                                    </Typography>
                                                                )}
                                                            </Box>
                                                            <ArrowForwardIosIcon sx={{ color: theme.palette.grey[600], fontSize: 16 }} />
                                                        </Box>
                                                    );
                                                })
                                            )}
                                        </Box>
                                        {selectedPlan && (
                                            <Alert severity="info" sx={{ mt: 2 }}>
                                                <Typography variant="body2">
                                                    <strong>Selected Plan:</strong> {selectedPlan.display_name} ({selectedPlan.provider_name.replace(/_/g, ' ')})
                                                </Typography>
                                                <Typography variant="caption" sx={{ mt: 0.5, display: 'block' }}>
                                                    Changing your electricity plan will cause all billing calculations to be redone. This action cannot be undone.
                                                </Typography>
                                            </Alert>
                                        )}
                                    </Grid>
                                    {isEditMode && (
                                        <>
                                            <Grid item xs={12}>
                                                <Divider sx={{ my: 2 }} />
                                            </Grid>
                                            <Grid item xs={12}>
                                                <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
                                                    Additional House Info
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12} md={6}>
                                                <TextField
                                                    fullWidth
                                                    name="dteuroanatm"
                                                    label="Municipal fees per m²"
                                                    type="number"
                                                    value={billingMetadata.dteuroanatm}
                                                    onChange={handleNumericBillingChange}
                                                    inputProps={{ min: 0, step: 'any' }}
                                                    disabled={billingMetadataLoading}
                                                />
                                            </Grid>
                                            <Grid item xs={12} md={6}>
                                                <TextField
                                                    fullWidth
                                                    name="dfeuroanatm"
                                                    label="Municipal taxes per m²"
                                                    type="number"
                                                    value={billingMetadata.dfeuroanatm}
                                                    onChange={handleNumericBillingChange}
                                                    inputProps={{ min: 0, step: 'any' }}
                                                    disabled={billingMetadataLoading}
                                                />
                                            </Grid>
                                            <Grid item xs={12} md={6}>
                                                <TextField
                                                    fullWidth
                                                    name="timizonis"
                                                    label="Zone Price"
                                                    type="number"
                                                    value={billingMetadata.timizonis}
                                                    onChange={handleNumericBillingChange}
                                                    inputProps={{ min: 0, step: 'any' }}
                                                    disabled={billingMetadataLoading}
                                                />
                                            </Grid>
                                            <Grid item xs={12} md={6}>
                                                <TextField
                                                    fullWidth
                                                    name="palaiotita"
                                                    label="Building Age"
                                                    type="number"
                                                    value={billingMetadata.palaiotita}
                                                    onChange={handleNumericBillingChange}
                                                    inputProps={{ min: 0, step: 'any' }}
                                                    disabled={billingMetadataLoading}
                                                />
                                            </Grid>
                                            <Grid item xs={12} md={6}>
                                                <TextField
                                                    fullWidth
                                                    name="synttap"
                                                    label="TAP Coefficient"
                                                    type="number"
                                                    value={billingMetadata.synttap}
                                                    onChange={handleNumericBillingChange}
                                                    inputProps={{ min: 0, step: 'any' }}
                                                    disabled={billingMetadataLoading}
                                                />
                                            </Grid>
                                            <Grid item xs={12} md={6}>
                                                <TextField
                                                    fullWidth
                                                    name="simfisxyspar"
                                                    label="Agreed Supply Power (kW)"
                                                    type="number"
                                                    value={billingMetadata.simfisxyspar}
                                                    onChange={handleNumericBillingChange}
                                                    inputProps={{ min: 0, step: 'any' }}
                                                    disabled={billingMetadataLoading}
                                                />
                                            </Grid>
                                            <Grid item xs={12} md={6}>
                                                <FormControl fullWidth>
                                                    <InputLabel>Night Tariff</InputLabel>
                                                    <Select
                                                        name="nightmeter"
                                                        value={billingMetadata.nightmeter}
                                                        onChange={handleBillingMetadataChange}
                                                        label="Night Tariff"
                                                        disabled={billingMetadataLoading}
                                                    >
                                                        <MenuItem value={false}>No</MenuItem>
                                                        <MenuItem value={true}>Yes</MenuItem>
                                                    </Select>
                                                </FormControl>
                                            </Grid>
                                        </>
                                    )}
                                </Grid>
                                <Box sx={{ mt: 3, mb: 2, width: '80%' }}>
                                    <Button
                                        sx={{
                                            color: 'white',
                                            width: '100%',
                                            background: 'linear-gradient(to right, #1976D2, #40C4FF)',
                                            boxShadow: 3,
                                            fontWeight: 600,
                                            letterSpacing: 1,
                                            '&:hover': {
                                                background: 'linear-gradient(to right, #1976D2, #40C4FF)',
                                            },
                                        }}
                                        type="submit"
                                        fullWidth
                                        variant="contained"
                                        color="secondary"
                                    >
                                        <Typography>{isEditMode ? 'EDIT' : 'ADD'}</Typography>
                                    </Button>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Fade>
                <LoadingFullPage backDrop={backDrop} setBackDrop={setBackDrop} />
                <Stack spacing={2} sx={{ width: '100%' }}>
                    <Snackbar open={success} autoHideDuration={2000} onClose={handleCloseSnackbar}>
                        <Alert variant="filled" onClose={handleCloseSnackbar} severity="success">
                            {isEditMode ? 'The house has been successfully updated.' : 'The house has been successfully added.'}
                        </Alert>
                    </Snackbar>
                    <Snackbar open={failure} autoHideDuration={3000} onClose={() => setFailure(false)}>
                        <Alert variant="filled" onClose={() => setFailure(false)} severity="error">
                            Something went wrong! Please try again.
                        </Alert>
                    </Snackbar>
                </Stack>
            </Container>
        </>
    );
};

export default HouseForm;
